import React, { useState, useEffect } from 'react';
import { productsAPI } from '../lib/api';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { 
  Plus, Search, MoreHorizontal, Pencil, Trash2, Loader2, Package, AlertTriangle, 
  RefreshCw, ChevronDown, ChevronRight, Layers, Box, ChevronLeft, ChevronsLeft, ChevronsRight, Settings2
} from 'lucide-react';
import { toast } from 'sonner';
import CatalogManager from '../components/CatalogManager';
import ProductImageManager from '../components/ProductImageManager';
import HtmlRichTextEditor from '../components/HtmlRichTextEditor';
import { productHtmlToPlainText } from '../lib/productHtml';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-LK', {
    style: 'currency',
    currency: 'LKR',
    minimumFractionDigits: 0,
  }).format(amount);
};

const initialFormData = {
  sku: '',
  name: '',
  description: '',
  short_description: '',
  category: '',
  categories: [],
  category_names: [],
  brand_id: '',
  brand: '',
  collection_id: '',
  collection_name: '',
  collection_slug: '',
  size_guide_id: '',
  size_guide_name: '',
  size_guide_slug: '',
  weight: '',
  visibility: 'public',
  tags: '',
  images: { featured: null, gallery: [] },
  meta_title: '',
  meta_description: '',
  meta_keywords: '',
  is_featured: false,
  free_shipping: false,
  regular_price: 0,
  sale_price: null,
};

const initialVariableFormData = {
  name: '',
  sku: '',
  description: '',
  short_description: '',
  category: '',
  categories: [],
  category_names: [],
  brand_id: '',
  brand: '',
  collection_id: '',
  collection_name: '',
  collection_slug: '',
  size_guide_id: '',
  size_guide_name: '',
  size_guide_slug: '',
  weight: '',
  visibility: 'public',
  tags: '',
  images: { featured: null, gallery: [] },
  meta_title: '',
  meta_description: '',
  meta_keywords: '',
  is_featured: false,
  attributes: [],
  variation_images: {},
  generate_variations: true,
};

const initialVariationEditFormData = {
  sku: '',
  attributes: [],
  weight: '',
  image: null,
  cost_price: 0,
  regular_price: 0,
  sale_price: null,
  selling_price: 0,
  stock_quantity: 0,
};

const combinationKey = (attributes = []) => attributes
  .map((attribute) => `${String(attribute.name || '').trim().toLowerCase()}=${String(attribute.option || '').trim().toLowerCase()}`)
  .join('|');

const buildVariationCombinations = (attributes = []) => {
  const validAttributes = attributes
    .filter((attribute) => String(attribute.name || '').trim() && String(attribute.options || '').trim())
    .map((attribute) => ({
      name: attribute.name.trim(),
      options: attribute.options.split(',').map((option) => option.trim()).filter(Boolean),
    }))
    .filter((attribute) => attribute.options.length > 0);
  if (!validAttributes.length) return [];

  const combinations = [];
  const walk = (index, current) => {
    if (index >= validAttributes.length) {
      const attributesForVariation = current.map((entry) => ({ ...entry }));
      combinations.push({
        attributes: attributesForVariation,
        key: combinationKey(attributesForVariation),
        label: attributesForVariation.map((entry) => entry.option).join(' - '),
      });
      return;
    }
    validAttributes[index].options.forEach((option) => {
      walk(index + 1, [...current, { name: validAttributes[index].name, option }]);
    });
  };
  walk(0, []);
  return combinations;
};

export const Products = () => {
  const [products, setProducts] = useState([]);
  const [variations, setVariations] = useState({});
  const [categories, setCategories] = useState([]);
  const [catalogCategories, setCatalogCategories] = useState([]);
  const [catalogCollections, setCatalogCollections] = useState([]);
  const [catalogBrands, setCatalogBrands] = useState([]);
  const [sizeGuides, setSizeGuides] = useState([]);
  const [catalogAttributes, setCatalogAttributes] = useState([]);
  const [loadingCatalogAttributes, setLoadingCatalogAttributes] = useState(false);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [collectionFilter, setCollectionFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [variableDialogOpen, setVariableDialogOpen] = useState(false);
  const [catalogManagerOpen, setCatalogManagerOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [variableFormData, setVariableFormData] = useState(initialVariableFormData);
  const [submitting, setSubmitting] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [expandedProducts, setExpandedProducts] = useState({});
  const [syncingVariations, setSyncingVariations] = useState(false);
  const [loadingVariations, setLoadingVariations] = useState({});
  const [variationEditDialogOpen, setVariationEditDialogOpen] = useState(false);
  const [selectedVariation, setSelectedVariation] = useState(null);
  const [selectedVariationParent, setSelectedVariationParent] = useState(null);
  const [variationEditFormData, setVariationEditFormData] = useState(initialVariationEditFormData);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 20,
    total_count: 0,
    total_pages: 1,
    has_next: false,
    has_prev: false
  });
  
  // Multi-select state
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const params = { page: currentPage, per_page: perPage };
      if (search) params.search = search;
      if (categoryFilter && categoryFilter !== 'all') params.category = categoryFilter;
      if (collectionFilter && collectionFilter !== 'all') params.collection_id = collectionFilter;
      
      const [productsRes, categoriesRes] = await Promise.all([
        productsAPI.getAll(params),
        productsAPI.getCategories(),
      ]);
      
      // Handle new paginated response format
      if (productsRes.data.products) {
        setProducts(productsRes.data.products);
        setPagination(productsRes.data.pagination);
      } else {
        // Fallback for old format
        setProducts(productsRes.data);
      }
      setCategories(categoriesRes.data);
    } catch (error) {
      toast.error('Failed to fetch products');
    } finally {
      setLoading(false);
    }
  };

  const fetchCatalogCategories = async () => {
    try {
      const response = await api.get('/catalog/categories');
      setCatalogCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch catalogue categories:', error);
    }
  };

  const fetchCatalogCollections = async () => {
    try {
      const response = await api.get('/catalog/collections');
      setCatalogCollections(response.data || []);
    } catch (error) {
      console.error('Failed to fetch product collections:', error);
    }
  };

  const fetchCatalogBrands = async () => {
    try {
      const response = await api.get('/catalog/brands');
      setCatalogBrands(response.data || []);
    } catch (error) {
      console.error('Failed to fetch product brands:', error);
    }
  };

  const fetchSizeGuides = async () => {
    try {
      const response = await api.get('/size-guides');
      setSizeGuides(response.data || []);
    } catch (error) {
      console.error('Failed to fetch size guides:', error);
      setSizeGuides([]);
    }
  };

  const fetchCatalogAttributes = async () => {
    setLoadingCatalogAttributes(true);
    try {
      const response = await api.get('/catalog/attributes');
      setCatalogAttributes(response.data || []);
    } catch (error) {
      console.error('Failed to fetch catalogue attributes:', error);
      setCatalogAttributes([]);
    } finally {
      setLoadingCatalogAttributes(false);
    }
  };

  const fetchVariations = async (productId) => {
    setLoadingVariations(prev => ({ ...prev, [productId]: true }));
    try {
      const response = await api.get(`/variations/product/${productId}`);
      setVariations(prev => ({
        ...prev,
        [productId]: response.data.variations
      }));
    } catch (error) {
      console.error('Failed to fetch variations:', error);
      toast.error('Failed to load variations');
    } finally {
      setLoadingVariations(prev => ({ ...prev, [productId]: false }));
    }
  };

  const syncAllVariations = async () => {
    setSyncingVariations(true);
    try {
      setVariations({});
      toast.success('Variation data refreshed from ERP');
    } finally {
      setSyncingVariations(false);
    }
  };

  const [syncingCategories, setSyncingCategories] = useState(false);
  
  const refreshCatalogCategories = async () => {
    setSyncingCategories(true);
    try {
      await fetchCatalogCategories();
      toast.success('Categories refreshed from ERP');
    } finally {
      setSyncingCategories(false);
    }
  };

  const toggleExpanded = (productId) => {
    const isExpanding = !expandedProducts[productId];
    setExpandedProducts(prev => ({
      ...prev,
      [productId]: isExpanding
    }));
    
    // Fetch variations if expanding and not already loaded
    if (isExpanding && !variations[productId]) {
      fetchVariations(productId);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, [currentPage, perPage, search, categoryFilter, collectionFilter]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [search, categoryFilter, collectionFilter]);

  useEffect(() => {
    fetchCatalogCategories();
    fetchCatalogCollections();
    fetchCatalogBrands();
    fetchSizeGuides();
    fetchCatalogAttributes();
  }, []);

  const handleOpenDialog = async (product = null) => {
    if (product) {
      setSelectedProduct(product);
      setFormData({
        sku: product.sku,
        name: product.name,
        description: product.description || '',
        short_description: product.short_description || '',
        category: product.category || '',
        categories: product.categories || [],
        category_names: product.category_names || [],
        brand_id: product.brand_id || '',
        brand: product.brand || '',
        collection_id: product.collection_id || '',
        collection_name: product.collection_name || '',
        collection_slug: product.collection_slug || '',
        size_guide_id: product.size_guide_id || '',
        size_guide_name: product.size_guide_name || '',
        size_guide_slug: product.size_guide_slug || '',
        weight: product.weight || '',
        visibility: product.visibility || 'public',
        tags: Array.isArray(product.tags) ? product.tags.join(', ') : (product.tags || ''),
        images: {
          featured: product.images?.featured?.url ? product.images.featured : null,
          gallery: (product.images?.gallery || []).filter((image) => image?.url),
        },
        meta_title: product.meta_title || product.name || '',
        meta_description: product.meta_description || productHtmlToPlainText(product.short_description || product.description || '').slice(0, 160),
        meta_keywords: product.meta_keywords || '',
        is_featured: Boolean(product.is_featured),
        free_shipping: Boolean(product.free_shipping),
        regular_price: Number(product.regular_price || product.selling_price || 0),
        sale_price: product.sale_price ?? null,
      });
      setSelectedCategories(product.categories || []);
      if (product.product_type === 'variable') {
        await fetchVariations(product.id);
      }
    } else {
      setSelectedProduct(null);
      setSelectedCategories([]);
      try {
        const skuRes = await api.get('/grn/next-sku');
        setFormData({
          ...initialFormData,
          sku: skuRes.data.next_sku
        });
      } catch (error) {
        setFormData(initialFormData);
      }
    }
    setDialogOpen(true);
  };

  const handleOpenVariableDialog = async () => {
    try {
      const skuRes = await api.get('/grn/next-sku');
      
      // Initialize reusable attributes from ERP product and variation data.
      let initialAttributes = [];
      if (catalogAttributes.length > 0) {
        initialAttributes = catalogAttributes.map(attr => ({
          id: attr.id || attr.name,
          name: attr.name,
          options: '',
          catalogOptions: attr.options.map((name, index) => ({ id: `${attr.name}-${index}`, name })),
          selectedOptions: []  // For multi-select
        }));
      } else {
        // Fallback to manual entry
        initialAttributes = [
          { name: 'Color', options: '', selectedOptions: [] },
          { name: 'Size', options: '', selectedOptions: [] }
        ];
      }
      
      setVariableFormData({
        ...initialVariableFormData,
        sku: skuRes.data.next_sku,
        attributes: initialAttributes
      });
    } catch (error) {
      setVariableFormData({
        ...initialVariableFormData,
        attributes: catalogAttributes.length > 0 
          ? catalogAttributes.map(attr => ({
              id: attr.id || attr.name,
              name: attr.name,
              options: '',
              catalogOptions: attr.options.map((name, index) => ({ id: `${attr.name}-${index}`, name })),
              selectedOptions: []
            }))
          : [
              { name: 'Color', options: '', selectedOptions: [] },
              { name: 'Size', options: '', selectedOptions: [] }
            ]
      });
    }
    setVariableDialogOpen(true);
  };

  const handleVariableAttributeChange = (index, field, value) => {
    const newAttributes = [...variableFormData.attributes];
    newAttributes[index] = { ...newAttributes[index], [field]: value };
    setVariableFormData({ ...variableFormData, attributes: newAttributes, variation_images: {} });
  };

  const handleAttributeOptionToggle = (attrIndex, optionName) => {
    const newAttributes = [...variableFormData.attributes];
    const attr = newAttributes[attrIndex];
    const currentSelected = attr.selectedOptions || [];
    
    if (currentSelected.includes(optionName)) {
      // Remove option
      attr.selectedOptions = currentSelected.filter(o => o !== optionName);
    } else {
      // Add option
      attr.selectedOptions = [...currentSelected, optionName];
    }
    
    // Update options string based on selected
    attr.options = attr.selectedOptions.join(', ');
    
    setVariableFormData({ ...variableFormData, attributes: newAttributes, variation_images: {} });
  };

  const addVariableAttribute = () => {
    setVariableFormData({
      ...variableFormData,
      attributes: [...variableFormData.attributes, { name: '', options: '', selectedOptions: [] }],
      variation_images: {},
    });
  };

  const removeVariableAttribute = (index) => {
    const newAttributes = variableFormData.attributes.filter((_, i) => i !== index);
    setVariableFormData({ ...variableFormData, attributes: newAttributes, variation_images: {} });
  };

  const handleVariableSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Validate attributes
      const validAttributes = variableFormData.attributes
        .filter(attr => attr.name.trim() && attr.options.trim())
        .map(attr => ({
          name: attr.name.trim(),
          options: attr.options.split(',').map(o => o.trim()).filter(o => o)
        }));

      if (validAttributes.length === 0) {
        toast.error('Please add at least one attribute with options');
        setSubmitting(false);
        return;
      }

      const combinations = buildVariationCombinations(variableFormData.attributes);
      const data = {
        name: variableFormData.name,
        sku: variableFormData.sku,
        description: variableFormData.description,
        short_description: variableFormData.short_description,
        category: variableFormData.category || (variableFormData.category_names && variableFormData.category_names[0]) || '',
        categories: variableFormData.categories || [],
        category_names: variableFormData.category_names || [],
        brand_id: variableFormData.brand_id || null,
        collection_id: variableFormData.collection_id || null,
        size_guide_id: variableFormData.size_guide_id || null,
        weight: variableFormData.weight ? parseFloat(variableFormData.weight) : null,
        visibility: variableFormData.visibility,
        tags: variableFormData.tags,
        images: variableFormData.images,
        meta_title: variableFormData.meta_title || variableFormData.name,
        meta_description: variableFormData.meta_description,
        meta_keywords: variableFormData.meta_keywords,
        is_featured: variableFormData.is_featured,
        attributes: validAttributes,
        variation_images: combinations
          .filter((combination) => variableFormData.variation_images?.[combination.key]?.url)
          .map((combination) => ({
            attributes: combination.attributes,
            image: variableFormData.variation_images[combination.key],
          })),
        generate_variations: variableFormData.generate_variations,
      };

      const response = await api.post('/variations/variable-product', data);
      
      toast.success(`Variable product draft created with ${response.data.variations_created} variations. It remains hidden until GRN or manufacturing receipt.`);
      
      setVariableDialogOpen(false);
      setVariableFormData(initialVariableFormData);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create variable product');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCategoryToggle = (categoryId, categoryName) => {
    setSelectedCategories(prev => {
      const isSelected = prev.includes(categoryId);
      if (isSelected) {
        const newCategories = prev.filter(id => id !== categoryId);
        const newNames = formData.category_names.filter(name => name !== categoryName);
        setFormData(f => ({ ...f, categories: newCategories, category_names: newNames }));
        return newCategories;
      } else {
        const newCategories = [...prev, categoryId];
        const newNames = [...(formData.category_names || []), categoryName];
        setFormData(f => ({ ...f, categories: newCategories, category_names: newNames }));
        return newCategories;
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const data = {
        sku: formData.sku,
        name: formData.name,
        description: formData.description,
        short_description: formData.short_description,
        category: formData.category || (formData.category_names && formData.category_names[0]) || '',
        categories: selectedCategories,
        category_names: formData.category_names || [],
        brand_id: formData.brand_id || null,
        collection_id: formData.collection_id || null,
        size_guide_id: formData.size_guide_id || null,
        weight: formData.weight ? parseFloat(formData.weight) : null,
        visibility: formData.visibility,
        tags: formData.tags,
        images: formData.images,
        meta_title: formData.meta_title || formData.name,
        meta_description: formData.meta_description,
        meta_keywords: formData.meta_keywords,
        is_featured: formData.is_featured,
        cost_price: 0,
        selling_price: 0,
        stock_quantity: 0,
        low_stock_threshold: 10,
      };

      if (selectedProduct) {
        await productsAPI.update(selectedProduct.id, {
          name: formData.name,
          description: formData.description,
          short_description: formData.short_description,
          category: formData.category || (formData.category_names && formData.category_names[0]) || '',
          categories: selectedCategories,
          category_names: formData.category_names || [],
          brand_id: formData.brand_id || '',
          collection_id: formData.collection_id || '',
          size_guide_id: formData.size_guide_id || '',
          weight: formData.weight ? parseFloat(formData.weight) : null,
          visibility: formData.visibility,
          tags: formData.tags,
          images: formData.images,
          meta_title: formData.meta_title || formData.name,
          meta_description: formData.meta_description,
          meta_keywords: formData.meta_keywords,
          is_featured: formData.is_featured,
          free_shipping: Boolean(formData.free_shipping),
          ...(selectedProduct?.product_type === 'simple' ? {
            regular_price: Number(formData.regular_price || 0),
            ...(formData.sale_price === '' || formData.sale_price === null
              ? { sale_price: null }
              : { sale_price: Number(formData.sale_price) }),
          } : {}),
        });
        toast.success('Product updated successfully');
      } else {
        await productsAPI.create(data);
        toast.success('Product draft created. It will remain hidden until a GRN or manufacturing receipt is confirmed.');
      }

      setDialogOpen(false);
      setSelectedCategories([]);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const openVariationEditor = (variation, parentProduct = null) => {
    const parent = parentProduct || products.find((product) => product.id === variation.parent_product_id) || null;
    setSelectedVariation(variation);
    setSelectedVariationParent(parent);
    setVariationEditFormData({
      sku: variation.sku || '',
      attributes: (variation.attributes || []).map((attribute) => ({
        name: attribute.name || '',
        option: attribute.option || '',
      })),
      weight: variation.weight ?? '',
      image: variation.image || (variation.image_url ? {
        url: variation.image_url,
        public_id: variation.image_public_id,
        alt: variation.variation_name,
      } : null),
      cost_price: Number(variation.cost_price || 0),
      regular_price: Number(variation.regular_price || 0),
      sale_price: variation.sale_price ?? null,
      selling_price: Number(variation.selling_price || 0),
      stock_quantity: Number(variation.stock_quantity || 0),
    });
    setVariationEditDialogOpen(true);
  };

  const handleVariationOptionChange = (index, option) => {
    setVariationEditFormData((current) => ({
      ...current,
      attributes: current.attributes.map((attribute, attributeIndex) => (
        attributeIndex === index ? { ...attribute, option } : attribute
      )),
    }));
  };

  const saveVariation = async () => {
    if (!selectedVariation) return;

    const sku = String(variationEditFormData.sku || '').trim();
    const attributes = (variationEditFormData.attributes || []).map((attribute) => ({
      name: String(attribute.name || '').trim(),
      option: String(attribute.option || '').trim(),
    }));
    if (!sku) {
      toast.error('Variation SKU is required');
      return;
    }
    if (!attributes.length || attributes.some((attribute) => !attribute.name || !attribute.option)) {
      toast.error('Every variation attribute requires an option');
      return;
    }
    if (variationEditFormData.weight !== '' && Number(variationEditFormData.weight) < 0) {
      toast.error('Variation weight cannot be negative');
      return;
    }

    setSubmitting(true);
    try {
      await api.put(`/variations/${selectedVariation.id}`, {
        sku,
        attributes,
        regular_price: Number(variationEditFormData.regular_price || 0),
        ...(variationEditFormData.sale_price === '' || variationEditFormData.sale_price === null
          ? { clear_sale_price: true }
          : { sale_price: Number(variationEditFormData.sale_price) }),
        weight: variationEditFormData.weight === '' ? null : Number(variationEditFormData.weight),
        clear_weight: variationEditFormData.weight === '',
        image: variationEditFormData.image,
        clear_image: !variationEditFormData.image,
      });
      toast.success('Variation updated successfully');
      setVariationEditDialogOpen(false);
      await fetchVariations(selectedVariation.parent_product_id);
      await fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update variation');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      await productsAPI.delete(selectedProduct.id);
      toast.success('Product deleted successfully');
      setDeleteDialogOpen(false);
      fetchProducts();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  // Multi-select functions
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedProducts(products.map(p => p.id));
    } else {
      setSelectedProducts([]);
    }
  };

  const handleSelectProduct = (productId, checked) => {
    if (checked) {
      setSelectedProducts(prev => [...prev, productId]);
    } else {
      setSelectedProducts(prev => prev.filter(id => id !== productId));
    }
  };

  const handleBulkDelete = async () => {
    setBulkDeleting(true);
    try {
      // Delete products one by one
      let successCount = 0;
      let failCount = 0;
      
      for (const productId of selectedProducts) {
        try {
          await productsAPI.delete(productId);
          successCount++;
        } catch (error) {
          failCount++;
        }
      }
      
      if (successCount > 0) {
        toast.success(`${successCount} product(s) deleted successfully`);
      }
      if (failCount > 0) {
        toast.error(`Failed to delete ${failCount} product(s)`);
      }
      
      setSelectedProducts([]);
      setBulkDeleteDialogOpen(false);
      fetchProducts();
    } catch (error) {
      toast.error('Bulk delete failed');
    } finally {
      setBulkDeleting(false);
    }
  };

  // Clear selection when page changes
  useEffect(() => {
    setSelectedProducts([]);
  }, [currentPage, perPage]);

  const getStockStatus = (quantity, threshold) => {
    if (quantity <= 0) {
      return <Badge className="badge-error">Out of Stock</Badge>;
    }
    if (quantity <= threshold) {
      return <Badge className="badge-warning">Low Stock</Badge>;
    }
    return <Badge className="badge-success">In Stock</Badge>;
  };

  const getProductTypeBadge = (product) => {
    if (product.product_type === 'variable') {
      return <Badge variant="outline" className="text-purple-600 border-purple-200 bg-purple-50">Variable</Badge>;
    }
    return <Badge variant="outline" className="text-gray-600">Simple</Badge>;
  };

  const getPublicationBadge = (product) => {
    if (product.publication_status === 'published' || product.storefront_eligible === true) {
      if (product.visibility === 'private') {
        return <Badge variant="outline" className="border-slate-300 bg-slate-100 text-slate-700">Received · Private</Badge>;
      }
      return <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Live after receipt</Badge>;
    }
    return <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Draft · Awaiting GRN</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="products-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Products
          </h2>
          <p className="text-slate-500 mt-1">{pagination.total_count} products in inventory</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={() => setCatalogManagerOpen(true)}
            className="gap-2"
          >
            <Settings2 className="w-4 h-4" />
            Categories &amp; Brands
          </Button>
          <Button 
            variant="outline" 
            onClick={syncAllVariations}
            disabled={syncingVariations}
            className="gap-2"
            data-testid="sync-variations-btn"
          >
            {syncingVariations ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Refresh Variations
          </Button>
          <Button 
            onClick={handleOpenVariableDialog} 
            className="gap-2 bg-purple-600 hover:bg-purple-700" 
            data-testid="add-variable-product-btn"
          >
            <Layers className="w-4 h-4" />
            Create Variable Product
          </Button>
          <Button onClick={() => handleOpenDialog()} className="gap-2 bg-indigo-600 hover:bg-indigo-700" data-testid="add-product-btn">
            <Plus className="w-4 h-4" />
            Add Simple Product
          </Button>
        </div>
      </div>

      <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
        <p className="font-bold">Product creation does not publish to the storefront.</p>
        <p className="mt-1 text-amber-800">The product stays as an internal draft with zero stock until a Purchase Order is received through GRN, or finished goods are confirmed through manufacturing. Public/private visibility applies only after that controlled receipt.</p>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search products..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="search-products"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full sm:w-48" data-testid="category-filter">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={collectionFilter} onValueChange={setCollectionFilter}>
              <SelectTrigger className="w-full sm:w-52" data-testid="collection-filter">
                <SelectValue placeholder="All Collections" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Collections</SelectItem>
                {catalogCollections.map((collection) => (
                  <SelectItem key={collection.id} value={collection.id}>{collection.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Products Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-16">
              <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900">No products found</h3>
              <p className="text-slate-500 mt-1">Get started by adding your first product.</p>
              <Button onClick={() => handleOpenDialog()} className="mt-4 bg-indigo-600 hover:bg-indigo-700">
                Add Product
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="table-header">
                  <TableHead className="table-header-cell w-10">
                    <input
                      type="checkbox"
                      checked={products.length > 0 && selectedProducts.length === products.length}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      data-testid="select-all-checkbox"
                    />
                  </TableHead>
                  <TableHead className="table-header-cell w-8"></TableHead>
                  <TableHead className="table-header-cell">Product</TableHead>
                  <TableHead className="table-header-cell">SKU</TableHead>
                  <TableHead className="table-header-cell">Type</TableHead>
                  <TableHead className="table-header-cell">Storefront</TableHead>
                  <TableHead className="table-header-cell">Category</TableHead>
                  <TableHead className="table-header-cell text-right">Cost</TableHead>
                  <TableHead className="table-header-cell text-right">Price</TableHead>
                  <TableHead className="table-header-cell text-right">Stock</TableHead>
                  <TableHead className="table-header-cell">Status</TableHead>
                  <TableHead className="table-header-cell w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.map((product) => (
                  <React.Fragment key={product.id}>
                    {/* Main Product Row */}
                    <TableRow 
                      className={`table-row ${selectedProducts.includes(product.id) ? 'bg-indigo-50' : ''}`} 
                      data-testid={`product-row-${product.id}`}
                    >
                      <TableCell className="table-cell">
                        <input
                          type="checkbox"
                          checked={selectedProducts.includes(product.id)}
                          onChange={(e) => handleSelectProduct(product.id, e.target.checked)}
                          className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          data-testid={`select-product-${product.id}`}
                        />
                      </TableCell>
                      <TableCell className="table-cell">
                        {product.product_type === 'variable' ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="p-0 h-6 w-6"
                            onClick={() => toggleExpanded(product.id)}
                            data-testid={`expand-btn-${product.id}`}
                          >
                            {expandedProducts[product.id] ? (
                              <ChevronDown className="w-4 h-4" />
                            ) : (
                              <ChevronRight className="w-4 h-4" />
                            )}
                          </Button>
                        ) : (
                          <Box className="w-4 h-4 text-slate-300" />
                        )}
                      </TableCell>
                      <TableCell className="table-cell font-medium">
                        <div>{product.name}</div>
                        {product.collection_name ? <span className="mt-1 inline-block text-xs font-normal text-indigo-600">Collection: {product.collection_name}</span> : null}
                      </TableCell>
                      <TableCell className="table-cell text-slate-500">{product.sku}</TableCell>
                      <TableCell className="table-cell">{getProductTypeBadge(product)}</TableCell>
                      <TableCell className="table-cell">{getPublicationBadge(product)}</TableCell>
                      <TableCell className="table-cell">
                        {product.category_names && product.category_names.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {product.category_names.map((cat, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs">
                                {cat}
                              </Badge>
                            ))}
                          </div>
                        ) : product.category ? (
                          <Badge variant="outline" className="text-xs">{product.category}</Badge>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="table-cell text-right">
                        {product.product_type === 'variable' && product.cost_price_min !== undefined && product.cost_price_max !== undefined && product.cost_price_min !== product.cost_price_max ? (
                          <span className="text-slate-700">
                            {formatCurrency(product.cost_price_min)} – {formatCurrency(product.cost_price_max)}
                          </span>
                        ) : (
                          formatCurrency(product.cost_price)
                        )}
                      </TableCell>
                      <TableCell className="table-cell text-right font-medium">
                        {product.product_type === 'variable' && product.selling_price_min !== undefined && product.selling_price_max !== undefined && product.selling_price_min !== product.selling_price_max ? (
                          <span>
                            {formatCurrency(product.selling_price_min)} – {formatCurrency(product.selling_price_max)}
                          </span>
                        ) : (
                          formatCurrency(product.selling_price)
                        )}
                      </TableCell>
                      <TableCell className="table-cell text-right">
                        <span className={product.stock_quantity <= product.low_stock_threshold ? 'text-amber-600 font-medium' : ''}>
                          {product.stock_quantity}
                        </span>
                      </TableCell>
                      <TableCell className="table-cell">{getStockStatus(product.stock_quantity, product.low_stock_threshold)}</TableCell>
                      <TableCell className="table-cell">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`product-menu-${product.id}`}>
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleOpenDialog(product)}>
                              <Pencil className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-red-600"
                              onClick={() => {
                                setSelectedProduct(product);
                                setDeleteDialogOpen(true);
                              }}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>

                    {/* Variations Rows (Expanded) */}
                    {expandedProducts[product.id] && (
                      <>
                        {loadingVariations[product.id] ? (
                          <TableRow className="bg-slate-50">
                            <TableCell colSpan={12} className="py-4 text-center">
                              <Loader2 className="w-5 h-5 animate-spin mx-auto text-indigo-600" />
                              <span className="text-sm text-slate-500 ml-2">Loading variations...</span>
                            </TableCell>
                          </TableRow>
                        ) : variations[product.id]?.length > 0 ? (
                          variations[product.id].map((variation) => (
                            <TableRow 
                              key={variation.id} 
                              className="bg-slate-50 border-l-4 border-l-purple-200"
                              data-testid={`variation-row-${variation.id}`}
                            >
                              <TableCell className="table-cell">
                                <Layers className="w-4 h-4 text-purple-400 ml-2" />
                              </TableCell>
                              <TableCell className="table-cell pl-8">
                                <div className="flex flex-col">
                                  <span className="font-medium text-slate-700">{variation.variation_name}</span>
                                  <div className="flex gap-1 mt-1">
                                    {variation.attributes?.map((attr, idx) => (
                                      <Badge key={idx} variant="secondary" className="text-xs">
                                        {attr.name}: {attr.option}
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell className="table-cell text-slate-500">{variation.sku}</TableCell>
                              <TableCell className="table-cell">
                                <Badge variant="outline" className="text-purple-500 text-xs">Variation</Badge>
                              </TableCell>
                              <TableCell className="table-cell text-slate-400">Follows parent</TableCell>
                              <TableCell className="table-cell text-slate-400">-</TableCell>
                              <TableCell className="table-cell text-right">{formatCurrency(variation.cost_price)}</TableCell>
                              <TableCell className="table-cell text-right font-medium">{formatCurrency(variation.selling_price)}</TableCell>
                              <TableCell className="table-cell text-right">
                                <span className={variation.stock_quantity <= 5 ? 'text-amber-600 font-medium' : ''}>
                                  {variation.stock_quantity}
                                </span>
                              </TableCell>
                              <TableCell className="table-cell">{getStockStatus(variation.stock_quantity, 5)}</TableCell>
                              <TableCell className="table-cell"><Button type="button" variant="outline" size="sm" onClick={() => openVariationEditor(variation, product)}><Pencil className="mr-1 h-3 w-3" /> Edit</Button></TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow className="bg-slate-50">
                            <TableCell colSpan={12} className="py-4 text-center text-slate-500">
                              No variations have been configured for this product.
                            </TableCell>
                          </TableRow>
                        )}
                      </>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {!loading && products.length > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-2">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <span>Showing</span>
            <Select value={String(perPage)} onValueChange={(value) => { setPerPage(Number(value)); setCurrentPage(1); }}>
              <SelectTrigger className="w-20 h-8" data-testid="per-page-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="20">20</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
            <span>of {pagination.total_count} products</span>
          </div>
          
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(1)}
              disabled={!pagination.has_prev}
              data-testid="first-page-btn"
            >
              <ChevronsLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={!pagination.has_prev}
              data-testid="prev-page-btn"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            
            <div className="flex items-center gap-1 px-2">
              {/* Page numbers */}
              {(() => {
                const pages = [];
                const totalPages = pagination.total_pages;
                const current = currentPage;
                
                // Always show first page
                if (totalPages > 0) {
                  pages.push(1);
                }
                
                // Show ellipsis if needed
                if (current > 3) {
                  pages.push('...');
                }
                
                // Show pages around current
                for (let i = Math.max(2, current - 1); i <= Math.min(totalPages - 1, current + 1); i++) {
                  if (!pages.includes(i)) {
                    pages.push(i);
                  }
                }
                
                // Show ellipsis if needed
                if (current < totalPages - 2) {
                  pages.push('...');
                }
                
                // Always show last page
                if (totalPages > 1 && !pages.includes(totalPages)) {
                  pages.push(totalPages);
                }
                
                return pages.map((page, idx) => (
                  page === '...' ? (
                    <span key={`ellipsis-${idx}`} className="px-2 text-slate-400">...</span>
                  ) : (
                    <Button
                      key={page}
                      variant={currentPage === page ? "default" : "outline"}
                      size="sm"
                      onClick={() => setCurrentPage(page)}
                      className={`w-8 h-8 p-0 ${currentPage === page ? 'bg-indigo-600 hover:bg-indigo-700' : ''}`}
                      data-testid={`page-${page}-btn`}
                    >
                      {page}
                    </Button>
                  )
                ));
              })()}
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.min(pagination.total_pages, prev + 1))}
              disabled={!pagination.has_next}
              data-testid="next-page-btn"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(pagination.total_pages)}
              disabled={!pagination.has_next}
              data-testid="last-page-btn"
            >
              <ChevronsRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Add/Edit Product Master Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[92vh] max-w-6xl overflow-y-auto" data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
              {selectedProduct ? 'Edit Product Master' : 'Create Simple Product'}
            </DialogTitle>
            <DialogDescription>
              Product descriptions, shipping details, catalogue assignments, images and SEO are maintained here. GRN only receives stock and costs.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-6 py-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="sku">SKU {!selectedProduct && '(Auto-generated)'}</Label>
                  <Input id="sku" value={formData.sku} onChange={(event) => setFormData({ ...formData, sku: event.target.value })} disabled={!!selectedProduct} placeholder="Leave blank to auto-generate" data-testid="product-sku" className={selectedProduct ? 'bg-slate-100' : ''} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="name">Product Name *</Label>
                  <Input id="name" value={formData.name} onChange={(event) => setFormData({ ...formData, name: event.target.value, meta_title: !formData.meta_title || formData.meta_title === formData.name ? event.target.value : formData.meta_title })} required data-testid="product-name" />
                </div>
              </div>

              <div className="grid gap-4 xl:grid-cols-2">
                <div className="space-y-2">
                  <Label>Full Product Description (HTML)</Label>
                  <HtmlRichTextEditor value={formData.description} onChange={(value) => setFormData({ ...formData, description: value })} placeholder="Complete product story, material, fit, care and features…" minHeight={190} ariaLabel="Full product HTML description" />
                </div>
                <div className="space-y-2">
                  <Label>Short Product Description (HTML)</Label>
                  <HtmlRichTextEditor value={formData.short_description} onChange={(value) => setFormData({ ...formData, short_description: value })} placeholder="Concise formatted summary near the product price…" minHeight={190} ariaLabel="Short product HTML description" />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Product Categories</Label>
                  <Button type="button" variant="outline" size="sm" onClick={refreshCatalogCategories} disabled={syncingCategories} className="h-7 text-xs">
                    {syncingCategories ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <RefreshCw className="mr-1 h-3 w-3" />} Refresh Categories
                  </Button>
                </div>
                <div className="max-h-48 overflow-y-auto rounded-lg border bg-slate-50 p-3">
                  <div className="grid gap-2 md:grid-cols-2">
                    {catalogCategories.map((cat) => (
                      <label key={cat.id} className="flex cursor-pointer items-center gap-2 rounded p-2 hover:bg-white">
                        <input type="checkbox" checked={selectedCategories.includes(String(cat.id))} onChange={() => handleCategoryToggle(String(cat.id), cat.name)} className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                        <span className="text-sm">{cat.parent_name ? `${cat.parent_name} / ` : ''}{cat.name}{cat.visibility === 'private' ? ' (Private)' : ''}</span>
                      </label>
                    ))}
                  </div>
                  {!catalogCategories.length ? <p className="py-4 text-center text-sm text-slate-500">No categories found. Create one in Catalogue Management.</p> : null}
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <div className="space-y-2">
                  <Label>Brand</Label>
                  <Select value={formData.brand_id || 'none'} onValueChange={(value) => { const id = value === 'none' ? '' : value; const selected = catalogBrands.find((item) => item.id === id); setFormData({ ...formData, brand_id: id, brand: selected?.name || '' }); }}>
                    <SelectTrigger><SelectValue placeholder="Select brand" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">No brand</SelectItem>{catalogBrands.filter((item) => item.is_active !== false).map((item) => <SelectItem key={item.id} value={item.id}>{item.name}{item.visibility === 'private' ? ' (Private)' : ''}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Collection</Label>
                  <Select value={formData.collection_id || 'none'} onValueChange={(value) => { const id = value === 'none' ? '' : value; const selected = catalogCollections.find((item) => item.id === id); setFormData({ ...formData, collection_id: id, collection_name: selected?.name || '', collection_slug: selected?.slug || '' }); }}>
                    <SelectTrigger><SelectValue placeholder="Select collection" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">No collection</SelectItem>{catalogCollections.filter((item) => item.is_active !== false).map((item) => <SelectItem key={item.id} value={item.id}>{item.name}{item.visibility === 'private' ? ' (Private)' : ''}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Size Guide</Label>
                  <Select value={formData.size_guide_id || 'none'} onValueChange={(value) => { const id = value === 'none' ? '' : value; const selected = sizeGuides.find((item) => item.id === id); setFormData({ ...formData, size_guide_id: id, size_guide_name: selected?.name || '', size_guide_slug: selected?.slug || '' }); }}>
                    <SelectTrigger><SelectValue placeholder="Select size guide" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">No size guide</SelectItem>{sizeGuides.filter((item) => item.is_active !== false).map((item) => <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Weight (kg)</Label>
                  <Input type="number" min="0" step="0.001" value={formData.weight} onChange={(event) => setFormData({ ...formData, weight: event.target.value })} placeholder="0.220" />
                  <p className="text-xs text-slate-500">Used by weight-based shipping.</p>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                <div className="space-y-2">
                  <Label>Storefront Visibility</Label>
                  <Select value={formData.visibility} onValueChange={(value) => setFormData({ ...formData, visibility: value })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="public">Public after stock receipt</SelectItem><SelectItem value="private">Private</SelectItem></SelectContent></Select>
                </div>
                <div className="space-y-2 md:col-span-1 xl:col-span-1">
                  <Label>Product Tags</Label>
                  <Input value={formData.tags} onChange={(event) => setFormData({ ...formData, tags: event.target.value })} placeholder="t-shirt, oversized, cotton" />
                </div>
                <div className="flex items-center gap-3 rounded-lg border bg-slate-50 px-4 py-3">
                  <Switch checked={formData.is_featured} onCheckedChange={(checked) => setFormData({ ...formData, is_featured: checked })} />
                  <div><Label>Featured Product</Label><p className="text-xs text-slate-500">Show in featured-product sections.</p></div>
                </div>
              </div>

              <ProductImageManager featuredImage={formData.images?.featured} galleryImages={formData.images?.gallery || []} productName={formData.name} onChange={({ featured, gallery }) => setFormData({ ...formData, images: { featured, gallery } })} disabled={submitting} />

              <div className="flex items-center justify-between gap-4 rounded-xl border border-emerald-200 bg-emerald-50/50 p-4">
                <div><p className="font-medium text-emerald-950">Free shipping for this product</p><p className="text-xs text-emerald-700">When enabled, orders containing this product receive free islandwide shipping. It does not change the product price.</p></div>
                <Switch checked={Boolean(formData.free_shipping)} onCheckedChange={(checked) => setFormData({ ...formData, free_shipping: checked })} data-testid="product-free-shipping" />
              </div>

              {selectedProduct?.product_type === 'simple' && (
                <div className="space-y-3 rounded-xl border border-emerald-200 bg-emerald-50/40 p-4">
                  <div><p className="font-medium text-emerald-950">Selling price</p><p className="text-xs text-emerald-700">You can change the selling price after GRN. The recorded cost remains locked to the GRN receipt.</p></div>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2"><Label>Regular Selling Price (LKR)</Label><Input type="number" min="0" step="0.01" value={formData.regular_price} onChange={(event) => setFormData({ ...formData, regular_price: event.target.value })} data-testid="product-edit-regular-price" /></div>
                    <div className="space-y-2"><Label>Sale Price (LKR)</Label><Input type="number" min="0" step="0.01" value={formData.sale_price ?? ''} onChange={(event) => setFormData({ ...formData, sale_price: event.target.value })} placeholder="Leave blank for no sale" data-testid="product-edit-sale-price" /></div>
                  </div>
                </div>
              )}

              <div className="space-y-4 rounded-xl border border-indigo-100 bg-indigo-50/40 p-4">
                <div><p className="font-medium text-indigo-950">Product SEO</p><p className="text-xs text-indigo-700">SEO information is stored on the product master and is no longer entered in GRN.</p></div>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2"><Label>SEO Title ({(formData.meta_title || '').length}/60)</Label><Input maxLength={60} value={formData.meta_title} onChange={(event) => setFormData({ ...formData, meta_title: event.target.value })} placeholder={formData.name || 'Product title'} /></div>
                  <div className="space-y-2"><Label>SEO Keywords</Label><Input value={formData.meta_keywords} onChange={(event) => setFormData({ ...formData, meta_keywords: event.target.value })} placeholder="comma, separated, keywords" /></div>
                </div>
                <div className="space-y-2"><Label>SEO Description ({(formData.meta_description || '').length}/160)</Label><Textarea rows={3} maxLength={160} value={formData.meta_description} onChange={(event) => setFormData({ ...formData, meta_description: event.target.value })} placeholder="Search-friendly product description" /><Button type="button" size="sm" variant="outline" onClick={() => setFormData({ ...formData, meta_description: productHtmlToPlainText(formData.short_description || formData.description).slice(0, 160) })}>Generate from description</Button></div>
              </div>

              {selectedProduct?.product_type === 'variable' && (
                <div className="space-y-4 rounded-xl border border-purple-200 bg-purple-50/30 p-4" data-testid="product-variation-editor-section">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="font-medium text-purple-950">Product Variations</p>
                      <p className="text-xs text-purple-700">Edit each variation's SKU, option values, weight override and Cloudinary image. Stock, cost and selling prices remain controlled by GRN and inventory workflows.</p>
                    </div>
                    <Button type="button" variant="outline" size="sm" onClick={() => fetchVariations(selectedProduct.id)} disabled={loadingVariations[selectedProduct.id]}>
                      {loadingVariations[selectedProduct.id] ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                      Refresh Variations
                    </Button>
                  </div>

                  {loadingVariations[selectedProduct.id] ? (
                    <div className="flex items-center justify-center rounded-lg border bg-white py-8 text-sm text-slate-500">
                      <Loader2 className="mr-2 h-5 w-5 animate-spin text-purple-600" /> Loading variations…
                    </div>
                  ) : variations[selectedProduct.id]?.length ? (
                    <div className="max-h-[26rem] overflow-auto rounded-lg border bg-white">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Variation</TableHead>
                            <TableHead>SKU</TableHead>
                            <TableHead className="text-right">Price</TableHead>
                            <TableHead className="text-right">Stock</TableHead>
                            <TableHead className="w-24"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {variations[selectedProduct.id].map((variation) => {
                            const imageUrl = variation.image?.url || variation.image_url || selectedProduct.images?.featured?.url;
                            return (
                              <TableRow key={variation.id} data-testid={`product-edit-variation-${variation.id}`}>
                                <TableCell>
                                  <div className="flex items-center gap-3">
                                    <div className="h-12 w-12 overflow-hidden rounded-md border bg-slate-100">
                                      {imageUrl ? <img src={imageUrl} alt={variation.variation_name || variation.sku} className="h-full w-full object-cover" /> : <Package className="m-3 h-6 w-6 text-slate-300" />}
                                    </div>
                                    <div>
                                      <p className="font-medium text-slate-800">{variation.variation_name}</p>
                                      <div className="mt-1 flex flex-wrap gap-1">
                                        {(variation.attributes || []).map((attribute, index) => <Badge key={`${attribute.name}-${index}`} variant="secondary" className="text-xs">{attribute.name}: {attribute.option}</Badge>)}
                                      </div>
                                    </div>
                                  </div>
                                </TableCell>
                                <TableCell className="text-slate-600">{variation.sku}</TableCell>
                                <TableCell className="text-right font-medium">{formatCurrency(variation.selling_price)}</TableCell>
                                <TableCell className="text-right">{variation.stock_quantity}</TableCell>
                                <TableCell><Button type="button" variant="outline" size="sm" onClick={() => openVariationEditor(variation, selectedProduct)}><Pencil className="mr-1 h-3 w-3" /> Edit</Button></TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <div className="rounded-lg border border-dashed bg-white py-8 text-center text-sm text-slate-500">No variations are configured for this product.</div>
                  )}
                </div>
              )}

              <p className="rounded-lg bg-blue-50 p-3 text-sm text-slate-600">Cost, selling prices and stock remain controlled by Purchase Orders, GRN and inventory workflows.</p>
            </div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button><Button type="submit" disabled={submitting} className="bg-indigo-600 hover:bg-indigo-700" data-testid="product-submit">{submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}{selectedProduct ? 'Update Product' : 'Create Product'}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent data-testid="delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Delete Product
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &quot;{selectedProduct?.name}&quot;? This action cannot be undone.
              {selectedProduct?.product_type === 'variable' && (
                <span className="block mt-2 text-amber-600">
                  Warning: This will also delete all associated variations.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Variable Product Dialog */}
      <Dialog open={variableDialogOpen} onOpenChange={setVariableDialogOpen}>
        <DialogContent className="max-h-[92vh] max-w-6xl overflow-y-auto" data-testid="variable-product-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }} className="flex items-center gap-2">
              <Layers className="w-5 h-5 text-purple-600" />
              Create Variable Product
            </DialogTitle>
            <DialogDescription>
              Create a product with variations (e.g., different colors and sizes). 
              All variation combinations will be auto-generated.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleVariableSubmit}>
            <div className="grid gap-4 py-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="var-sku">SKU (Base) *</Label>
                  <Input
                    id="var-sku"
                    value={variableFormData.sku}
                    onChange={(e) => setVariableFormData({ ...variableFormData, sku: e.target.value })}
                    required
                    placeholder="TROUSER-001"
                    data-testid="variable-product-sku"
                  />
                  <p className="text-xs text-slate-400">Variations will have suffixes like -BLU-S, -BLK-M</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="var-name">Product Name *</Label>
                  <Input
                    id="var-name"
                    value={variableFormData.name}
                    onChange={(e) => setVariableFormData({ ...variableFormData, name: e.target.value })}
                    required
                    placeholder="Classic Trouser"
                    data-testid="variable-product-name"
                  />
                </div>
              </div>

              <div className="grid gap-4 xl:grid-cols-2">
                <div className="space-y-2"><Label>Full Product Description (HTML)</Label><HtmlRichTextEditor value={variableFormData.description} onChange={(value) => setVariableFormData({ ...variableFormData, description: value })} placeholder="Complete shared description for all variations…" minHeight={180} ariaLabel="Variable product full HTML description" /></div>
                <div className="space-y-2"><Label>Short Product Description (HTML)</Label><HtmlRichTextEditor value={variableFormData.short_description} onChange={(value) => setVariableFormData({ ...variableFormData, short_description: value })} placeholder="Concise shared summary for all variations…" minHeight={180} ariaLabel="Variable product short HTML description" /></div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Categories (Select Multiple)</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={refreshCatalogCategories}
                    disabled={syncingCategories}
                    className="h-7 text-xs"
                  >
                    {syncingCategories ? (
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    ) : (
                      <RefreshCw className="w-3 h-3 mr-1" />
                    )}
                    Refresh Categories
                  </Button>
                </div>
                <div className="border rounded-lg p-3 max-h-36 overflow-y-auto bg-slate-50">
                  {catalogCategories.length > 0 ? (
                    <div className="grid grid-cols-2 gap-2">
                      {catalogCategories.map((cat) => (
                        <label 
                          key={cat.id} 
                          className="flex items-center gap-2 p-2 rounded hover:bg-white cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={variableFormData.categories?.includes(String(cat.id))}
                            onChange={() => {
                              const catId = String(cat.id);
                              const isSelected = variableFormData.categories?.includes(catId);
                              if (isSelected) {
                                setVariableFormData(f => ({
                                  ...f,
                                  categories: f.categories.filter(c => c !== catId),
                                  category_names: f.category_names.filter(n => n !== cat.name),
                                  category: f.category_names.filter(n => n !== cat.name)[0] || ''
                                }));
                              } else {
                                setVariableFormData(f => ({
                                  ...f,
                                  categories: [...(f.categories || []), catId],
                                  category_names: [...(f.category_names || []), cat.name],
                                  category: f.category || cat.name
                                }));
                              }
                            }}
                            className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <span className="text-sm">{cat.name}{cat.visibility === 'private' ? ' (Private — storefront hidden)' : ''}</span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-3">
                      <p className="text-sm text-gray-500">No categories found.</p>
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        onClick={refreshCatalogCategories}
                        disabled={syncingCategories}
                        className="text-indigo-600"
                      >
                        {syncingCategories ? 'Refreshing...' : 'Refresh categories'}
                      </Button>
                    </div>
                  )}
                </div>
                {variableFormData.category_names?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {variableFormData.category_names.map((name, idx) => (
                      <Badge key={idx} variant="secondary" className="text-xs">
                        {name}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <div className="space-y-2"><Label>Brand</Label><Select value={variableFormData.brand_id || 'none'} onValueChange={(value) => { const id = value === 'none' ? '' : value; const selected = catalogBrands.find((item) => item.id === id); setVariableFormData({ ...variableFormData, brand_id: id, brand: selected?.name || '' }); }}><SelectTrigger><SelectValue placeholder="Select brand" /></SelectTrigger><SelectContent><SelectItem value="none">No brand</SelectItem>{catalogBrands.filter((item) => item.is_active !== false).map((item) => <SelectItem key={item.id} value={item.id}>{item.name}{item.visibility === 'private' ? ' (Private)' : ''}</SelectItem>)}</SelectContent></Select></div>
                <div className="space-y-2"><Label>Collection</Label><Select value={variableFormData.collection_id || 'none'} onValueChange={(value) => { const id = value === 'none' ? '' : value; const selected = catalogCollections.find((item) => item.id === id); setVariableFormData({ ...variableFormData, collection_id: id, collection_name: selected?.name || '', collection_slug: selected?.slug || '' }); }}><SelectTrigger><SelectValue placeholder="Select collection" /></SelectTrigger><SelectContent><SelectItem value="none">No collection</SelectItem>{catalogCollections.filter((item) => item.is_active !== false).map((item) => <SelectItem key={item.id} value={item.id}>{item.name}{item.visibility === 'private' ? ' (Private)' : ''}</SelectItem>)}</SelectContent></Select></div>
                <div className="space-y-2"><Label>Size Guide</Label><Select value={variableFormData.size_guide_id || 'none'} onValueChange={(value) => { const id = value === 'none' ? '' : value; const selected = sizeGuides.find((item) => item.id === id); setVariableFormData({ ...variableFormData, size_guide_id: id, size_guide_name: selected?.name || '', size_guide_slug: selected?.slug || '' }); }}><SelectTrigger><SelectValue placeholder="Select size guide" /></SelectTrigger><SelectContent><SelectItem value="none">No size guide</SelectItem>{sizeGuides.filter((item) => item.is_active !== false).map((item) => <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>)}</SelectContent></Select></div>
                <div className="space-y-2"><Label>Weight (kg)</Label><Input type="number" min="0" step="0.001" value={variableFormData.weight} onChange={(event) => setVariableFormData({ ...variableFormData, weight: event.target.value })} placeholder="0.220" /><p className="text-xs text-slate-500">Default weight for every generated variation.</p></div>
              </div>
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2"><Label>Storefront Visibility</Label><Select value={variableFormData.visibility} onValueChange={(value) => setVariableFormData({ ...variableFormData, visibility: value })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="public">Public after stock receipt</SelectItem><SelectItem value="private">Private</SelectItem></SelectContent></Select></div>
                <div className="space-y-2"><Label>Product Tags</Label><Input value={variableFormData.tags} onChange={(event) => setVariableFormData({ ...variableFormData, tags: event.target.value })} placeholder="oversized, cotton, unisex" /></div>
                <div className="flex items-center gap-3 rounded-lg border bg-slate-50 px-4 py-3"><Switch checked={variableFormData.is_featured} onCheckedChange={(checked) => setVariableFormData({ ...variableFormData, is_featured: checked })} /><div><Label>Featured Product</Label><p className="text-xs text-slate-500">Applies to the parent product.</p></div></div>
              </div>
              <ProductImageManager featuredImage={variableFormData.images?.featured} galleryImages={variableFormData.images?.gallery || []} productName={variableFormData.name} title="Shared parent product images" description="Main and gallery images used by all variations unless a specific variation image is selected below." onChange={({ featured, gallery }) => setVariableFormData({ ...variableFormData, images: { featured, gallery } })} disabled={submitting} />
              <div className="space-y-4 rounded-xl border border-indigo-100 bg-indigo-50/40 p-4">
                <p className="font-medium text-indigo-950">Product SEO</p>
                <div className="grid gap-4 md:grid-cols-2"><div className="space-y-2"><Label>SEO Title ({(variableFormData.meta_title || '').length}/60)</Label><Input maxLength={60} value={variableFormData.meta_title} onChange={(event) => setVariableFormData({ ...variableFormData, meta_title: event.target.value })} placeholder={variableFormData.name || 'Product title'} /></div><div className="space-y-2"><Label>SEO Keywords</Label><Input value={variableFormData.meta_keywords} onChange={(event) => setVariableFormData({ ...variableFormData, meta_keywords: event.target.value })} placeholder="comma, separated, keywords" /></div></div>
                <div className="space-y-2"><Label>SEO Description ({(variableFormData.meta_description || '').length}/160)</Label><Textarea rows={3} maxLength={160} value={variableFormData.meta_description} onChange={(event) => setVariableFormData({ ...variableFormData, meta_description: event.target.value })} placeholder="Search-friendly product description" /><Button type="button" size="sm" variant="outline" onClick={() => setVariableFormData({ ...variableFormData, meta_description: productHtmlToPlainText(variableFormData.short_description || variableFormData.description).slice(0, 160) })}>Generate from description</Button></div>
              </div>

              {/* Attributes Section */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-medium">Variation Attributes</Label>
                  {catalogAttributes.length === 0 && (
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm" 
                      onClick={addVariableAttribute}
                      className="gap-1"
                    >
                      <Plus className="w-3 h-3" /> Add Attribute
                    </Button>
                  )}
                </div>
                
                {loadingCatalogAttributes ? (
                  <div className="flex items-center gap-2 text-sm text-slate-500 py-4">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading attributes from ERP...
                  </div>
                ) : catalogAttributes.length > 0 ? (
                  <>
                    <p className="text-sm text-green-600 bg-green-50 p-2 rounded">
                      ✓ Select options from your ERP catalogue attributes below
                    </p>
                    <div className="space-y-4 border rounded-lg p-4 bg-slate-50">
                      {variableFormData.attributes.map((attr, idx) => (
                        <div key={idx} className="space-y-2">
                          <div className="flex items-center justify-between">
                            <Label className="font-medium text-slate-700">{attr.name}</Label>
                            {attr.selectedOptions?.length > 0 && (
                              <Badge variant="secondary" className="text-xs">
                                {attr.selectedOptions.length} selected
                              </Badge>
                            )}
                          </div>
                          {attr.catalogOptions && attr.catalogOptions.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                              {attr.catalogOptions.map((opt) => (
                                <label 
                                  key={opt.id || opt.name} 
                                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full border cursor-pointer transition-colors ${
                                    (attr.selectedOptions || []).includes(opt.name)
                                      ? 'bg-purple-100 border-purple-400 text-purple-700'
                                      : 'bg-white border-slate-200 hover:border-purple-300'
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={(attr.selectedOptions || []).includes(opt.name)}
                                    onChange={() => handleAttributeOptionToggle(idx, opt.name)}
                                    className="w-3.5 h-3.5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                                  />
                                  <span className="text-sm">{opt.name}</span>
                                </label>
                              ))}
                            </div>
                          ) : (
                            <Input
                              value={attr.options}
                              onChange={(e) => handleVariableAttributeChange(idx, 'options', e.target.value)}
                              placeholder="Enter options separated by commas"
                              className="text-sm"
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-slate-500">
                      No saved attributes found. Enter options manually below.
                    </p>
                    <div className="space-y-3 border rounded-lg p-4 bg-slate-50">
                      {variableFormData.attributes.map((attr, idx) => (
                        <div key={idx} className="flex gap-3 items-start">
                          <div className="flex-1">
                            <Label className="text-xs text-slate-500">Attribute Name</Label>
                            <Input
                              value={attr.name}
                              onChange={(e) => handleVariableAttributeChange(idx, 'name', e.target.value)}
                              placeholder="e.g., Color, Size"
                              data-testid={`attr-name-${idx}`}
                            />
                          </div>
                          <div className="flex-[2]">
                            <Label className="text-xs text-slate-500">Options (comma separated)</Label>
                            <Input
                              value={attr.options}
                              onChange={(e) => handleVariableAttributeChange(idx, 'options', e.target.value)}
                              placeholder="e.g., Blue, Black, Red or S, M, L, XL"
                              data-testid={`attr-options-${idx}`}
                            />
                          </div>
                          {variableFormData.attributes.length > 1 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => removeVariableAttribute(idx)}
                              className="mt-5 text-red-500 hover:text-red-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {/* Preview */}
                {variableFormData.attributes.filter(a => a.name && a.options).length > 0 && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                    <p className="text-sm font-medium text-purple-700 mb-2">Preview: Variations to be created</p>
                    <div className="flex flex-wrap gap-1">
                      {(() => {
                        const validAttrs = variableFormData.attributes
                          .filter(a => a.name.trim() && a.options.trim())
                          .map(a => ({
                            name: a.name,
                            options: a.options.split(',').map(o => o.trim()).filter(o => o)
                          }));
                        
                        if (validAttrs.length === 0) return null;
                        
                        // Calculate total combinations
                        const totalCombos = validAttrs.reduce((acc, attr) => acc * attr.options.length, 1);
                        
                        // Show first few combinations
                        const firstOptions = validAttrs.map(a => a.options.slice(0, 2));
                        const sampleCombos = [];
                        
                        const generateSamples = (current, attrIdx) => {
                          if (attrIdx >= firstOptions.length) {
                            sampleCombos.push(current.join(' - '));
                            return;
                          }
                          for (const opt of firstOptions[attrIdx]) {
                            if (sampleCombos.length < 4) {
                              generateSamples([...current, opt], attrIdx + 1);
                            }
                          }
                        };
                        generateSamples([], 0);
                        
                        return (
                          <>
                            {sampleCombos.map((combo, i) => (
                              <Badge key={i} variant="secondary" className="text-xs">
                                {variableFormData.name || 'Product'} - {combo}
                              </Badge>
                            ))}
                            {totalCombos > 4 && (
                              <Badge variant="outline" className="text-xs text-purple-600">
                                +{totalCombos - 4} more
                              </Badge>
                            )}
                            <span className="text-xs text-purple-600 ml-2">
                              Total: {totalCombos} variations
                            </span>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                )}
              </div>

              {buildVariationCombinations(variableFormData.attributes).length > 0 && (
                <div className="space-y-4 rounded-xl border border-purple-200 bg-purple-50/40 p-4">
                  <div><p className="font-medium text-purple-950">Variation-specific images</p><p className="text-xs text-purple-700">Optional. Choose a different image for each generated color/variation. The same Cloudinary image may be reused across sizes.</p></div>
                  <div className="grid gap-4 xl:grid-cols-2">
                    {buildVariationCombinations(variableFormData.attributes).map((combination) => (
                      <ProductImageManager key={combination.key} featuredImage={variableFormData.variation_images?.[combination.key] || null} galleryImages={[]} productName={`${variableFormData.name || 'Product'} - ${combination.label}`} singleImage title={`${variableFormData.name || 'Product'} — ${combination.label}`} description={combination.attributes.map((attribute) => `${attribute.name}: ${attribute.option}`).join(' · ')} onChange={({ featured }) => setVariableFormData((current) => ({ ...current, variation_images: { ...(current.variation_images || {}), [combination.key]: featured } }))} disabled={submitting} />
                    ))}
                  </div>
                </div>
              )}
              <p className="rounded-lg bg-purple-50 p-3 text-sm text-slate-500">After creating the product master, use Purchase Orders and GRN only to receive stock and set cost/selling prices for each variation.</p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setVariableDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={submitting} 
                className="bg-purple-600 hover:bg-purple-700" 
                data-testid="variable-product-submit"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Layers className="w-4 h-4 mr-2" />}
                Create Variable Product
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={variationEditDialogOpen} onOpenChange={setVariationEditDialogOpen}>
        <DialogContent className="max-h-[92vh] max-w-4xl overflow-y-auto" data-testid="variation-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit Product Variation</DialogTitle>
            <DialogDescription>{selectedVariation?.variation_name || 'Variation'} — update the variation setup and customer-facing image.</DialogDescription>
          </DialogHeader>

          <div className="grid gap-5 py-2">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Parent Product</Label>
                <Input value={selectedVariationParent?.name || selectedVariation?.parent_product_name || ''} disabled className="bg-slate-100" />
              </div>
              <div className="space-y-2">
                <Label>Variation SKU *</Label>
                <Input value={variationEditFormData.sku} onChange={(event) => setVariationEditFormData((current) => ({ ...current, sku: event.target.value }))} placeholder="Unique variation SKU" data-testid="variation-edit-sku" />
              </div>
            </div>

            <div className="space-y-3 rounded-lg border bg-slate-50 p-4">
              <div>
                <p className="font-medium text-slate-900">Variation Options</p>
                <p className="text-xs text-slate-500">Change the selected option for this variation. Attribute names are controlled by the parent variable product.</p>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                {variationEditFormData.attributes.map((attribute, index) => (
                  <div key={`${attribute.name}-${index}`} className="space-y-2">
                    <Label>{attribute.name || `Attribute ${index + 1}`}</Label>
                    <Input value={attribute.option} onChange={(event) => handleVariationOptionChange(index, event.target.value)} placeholder={`Enter ${attribute.name || 'option'}`} data-testid={`variation-edit-option-${index}`} />
                  </div>
                ))}
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <div className="space-y-2">
                <Label>Weight Override (kg)</Label>
                <Input type="number" min="0" step="0.001" value={variationEditFormData.weight} onChange={(event) => setVariationEditFormData((current) => ({ ...current, weight: event.target.value }))} placeholder="Blank uses parent weight" data-testid="variation-edit-weight" />
                <p className="text-xs text-slate-500">Leave blank to use the parent product weight.</p>
              </div>
              <div className="space-y-2">
                <Label>Current Stock</Label>
                <Input value={variationEditFormData.stock_quantity} disabled className="bg-slate-100" />
              </div>
              <div className="space-y-2">
                <Label>Current Cost</Label>
                <Input value={formatCurrency(variationEditFormData.cost_price)} disabled className="bg-slate-100" />
              </div>
              <div className="space-y-2">
                <Label>Regular Selling Price (LKR)</Label>
                <Input type="number" min="0" step="0.01" value={variationEditFormData.regular_price} onChange={(event) => setVariationEditFormData((current) => ({ ...current, regular_price: event.target.value }))} data-testid="variation-edit-regular-price" />
              </div>
              <div className="space-y-2">
                <Label>Sale Price (LKR)</Label>
                <Input type="number" min="0" step="0.01" value={variationEditFormData.sale_price ?? ''} onChange={(event) => setVariationEditFormData((current) => ({ ...current, sale_price: event.target.value }))} placeholder="Leave blank for no sale" data-testid="variation-edit-sale-price" />
              </div>
            </div>

            <ProductImageManager featuredImage={variationEditFormData.image} galleryImages={[]} productName={selectedVariation?.variation_name || 'Variation'} singleImage title="Variation-specific image" description="This image replaces the parent product image when a customer selects this variation. Remove it to fall back to the parent image." onChange={({ featured }) => setVariationEditFormData((current) => ({ ...current, image: featured }))} disabled={submitting} />

            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              Cost and stock remain controlled by GRN/manufacturing. Selling prices can be updated here at any time without changing the recorded cost.
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setVariationEditDialogOpen(false)}>Cancel</Button>
            <Button type="button" onClick={saveVariation} disabled={submitting} data-testid="variation-edit-save">
              {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Save Variation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete {selectedProducts.length} Products</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {selectedProducts.length} selected product(s)? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800">
                <strong>Warning:</strong> This will permanently delete the selected products and all associated data including variations and inventory records.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDeleteDialogOpen(false)} disabled={bulkDeleting}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleBulkDelete} 
              disabled={bulkDeleting}
              data-testid="confirm-bulk-delete-btn"
            >
              {bulkDeleting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete {selectedProducts.length} Products
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Floating Bulk Action Toolbar */}
      {selectedProducts.length > 0 && (
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50">
          <div className="bg-slate-900 text-white px-6 py-3 rounded-full shadow-2xl flex items-center gap-4">
            <span className="text-sm font-medium">
              {selectedProducts.length} product{selectedProducts.length > 1 ? 's' : ''} selected
            </span>
            <div className="h-4 w-px bg-slate-600"></div>
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-white hover:bg-slate-700 hover:text-white"
              onClick={() => setSelectedProducts([])}
            >
              Clear Selection
            </Button>
            <Button 
              variant="destructive" 
              size="sm"
              onClick={() => setBulkDeleteDialogOpen(true)}
              data-testid="bulk-delete-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Selected
            </Button>
          </div>
        </div>
      )}

      <CatalogManager
        open={catalogManagerOpen}
        onOpenChange={setCatalogManagerOpen}
        onChanged={() => {
          fetchCatalogCategories();
          fetchProducts();
        }}
      />
    </div>
  );
};

export default Products;
